import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { auth0Config } from './config/auth0Config';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FamilyProvider, useFamily } from './contexts/FamilyContext';
import { InvitationProvider, useInvitation } from './contexts/InvitationContext';
import { ToastProvider } from './contexts/ToastContext';
import { SearchProvider } from './contexts/SearchContext';
import { PermissionsProvider, usePermissions } from './contexts/PermissionsContext';
import { CalendarProvider } from './contexts/CalendarContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ReferralProvider } from './contexts/ReferralContext';
import { RedeemProvider } from './contexts/RedeemContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import UpgradeModal from './components/UpgradeModal';
import { userAPI } from './services/api';
import { getScreenForStep, ONBOARDING_STEPS } from './constants/onboardingSteps';
// Freemium-redesign Phase 1 C1: single quickstart screen replaces the legacy
// multi-step onboarding chain. The legacy pages (OnboardingFamilySetup,
// OnboardingSubscription, OnboardingInviteFamilyMembers, OnboardingUserType,
// OnboardingAthleteProfile, OnboardingSchoolInfo, OnboardingSportInfo,
// OnboardingAcademicPerformance, OnboardingSportPerformance, OnboardingProfilePic,
// OnboardingConnectSocials, OnboardingComplete) are no longer imported here —
// their routes are <Navigate> shims below. Phase 2 deletes the page files from
// disk once mobile binary lifecycle clears.
import OnboardingQuickStart from './pages/OnboardingQuickStart';
// InvitationWelcome is now mounted by InvitationRouter for the soft-warn
// case only; it's no longer routed directly.
import InvitationRouter from './pages/InvitationRouter';
import AccountDeletionSplash from './pages/AccountDeletionSplash';
import SubscriptionRequired from './pages/SubscriptionRequired';
import SubscriptionGate from './components/SubscriptionGate';
import Dashboard from './pages/Dashboard';
import PlayerProfile from './pages/PlayerProfile';
import CollegeLists from './pages/CollegeLists';
import Messages from './pages/Messages';
import OAuthCallback from './pages/OAuthCallback';
import SignUpPage from './pages/SignUpPage';
import LoginPage from './pages/LoginPage';
import EmailVerifiedPage from './pages/EmailVerifiedPage';
import Connections from './pages/Connections';
import NotificationSettings from './pages/NotificationSettings';
import { CoachRoute, CoachHome } from './components/CoachRoute';
import CoachSurfaceTheme from './components/coach/CoachSurfaceTheme';
import GamePlanEmailClickTracker from './components/gameplan/GamePlanEmailClickTracker';
import AppErrorBoundary from './components/AppErrorBoundary';
import './App.css';

// Code-splitting (#145): the analytics/admin/staff pages pull heavy libraries
// (recharts, etc.) and are used by a minority of sessions. React.lazy keeps
// them out of the initial bundle so first paint doesn't download 11 admin pages
// + charts for everyone. Route elements are wrapped in a single <Suspense>.
const Analytics = React.lazy(() => import('./pages/Analytics'));
const PublicProfile = React.lazy(() => import('./pages/PublicProfile'));
const RedeemPage = React.lazy(() => import('./pages/RedeemPage'));
const PublicSchool = React.lazy(() => import('./pages/PublicSchool'));
const Tutorials = React.lazy(() => import('./pages/Tutorials'));
const AdminUsers = React.lazy(() => import('./pages/AdminUsers'));
const AdminManageAthletes = React.lazy(() => import('./pages/AdminManageAthletes'));
const AdminAthleteProfile = React.lazy(() => import('./pages/AdminAthleteProfile'));
const AdminQueues = React.lazy(() => import('./pages/AdminQueues'));
const AdminPromoCodes = React.lazy(() => import('./pages/AdminPromoCodes'));
const AdminPartners = React.lazy(() => import('./pages/AdminPartners'));
const AdminPartnerDetail = React.lazy(() => import('./pages/AdminPartnerDetail'));
const AdminPayouts = React.lazy(() => import('./pages/AdminPayouts'));
const AdminOrganizations = React.lazy(() => import('./pages/AdminOrganizations'));
const AdminOrgDetail = React.lazy(() => import('./pages/AdminOrgDetail'));
const AdminAnalytics = React.lazy(() => import('./pages/AdminAnalytics'));
// The internal college-coach verification desk (KGH-795): the approval queue,
// every coach account, and the invites that let a coach skip the queue.
const AdminCoaches = React.lazy(() => import('./pages/AdminCoaches'));
// Staging only: every email staging sends. The test is written out rather than
// read from config/environment so webpack sees a build-time constant here and
// leaves the page's chunk out of a production bundle altogether.
const AdminEmailOutbox = process.env.REACT_APP_ENV === 'staging'
  ? React.lazy(() => import('./pages/AdminEmailOutbox'))
  : null;
const StaffRoster = React.lazy(() => import('./pages/StaffRoster'));
// The college-coach portal (KGH-795). A coach is a third role, neither family
// nor staff, and every guard below has to know that or they land on a family
// dashboard whose every destination 403s.
const CoachPlayers = React.lazy(() => import('./pages/CoachPlayers'));
const CoachPlayerProfile = React.lazy(() => import('./pages/CoachPlayerProfile'));
const CoachLists = React.lazy(() => import('./pages/CoachLists'));
const CoachTournaments = React.lazy(() => import('./pages/CoachTournaments'));
const StaffAthleteProfile = React.lazy(() => import('./pages/StaffAthleteProfile'));
const StaffMessages = React.lazy(() => import('./pages/StaffMessages'));
const StaffAnalytics = React.lazy(() => import('./pages/StaffAnalytics'));
const StaffLists = React.lazy(() => import('./pages/StaffLists'));
const StaffAllSchools = React.lazy(() => import('./pages/StaffAllSchools'));
const StaffSchoolAggregation = React.lazy(() => import('./pages/StaffSchoolAggregation'));
const StaffOnboardingProfile = React.lazy(() => import('./pages/StaffOnboardingProfile'));
const StaffOnboardingConnectSocials = React.lazy(() => import('./pages/StaffOnboardingConnectSocials'));
// College coach portal (KGH-795). Lazy for the same reason as staff/admin: no
// family session should download a coach page it can never open.
const CoachSignup = React.lazy(() => import('./pages/CoachSignup'));
const CoachPending = React.lazy(() => import('./pages/CoachPending'));
const CoachSettings = React.lazy(() => import('./pages/CoachSettings'));

const DashboardPage = () => {
  const { user, logout } = useAuth();
  
  return (
    <div style={{ padding: '40px' }}>
      <h1>Dashboard</h1>
      <p>Welcome, {user?.email}!</p>
      <pre style={{ background: '#f5f5f5', padding: '20px', borderRadius: '8px' }}>
        {JSON.stringify(user, null, 2)}
      </pre>
      <button 
        onClick={logout}
        style={{
          marginTop: '20px',
          padding: '12px 24px',
          fontSize: '16px',
          background: '#dc3545',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer'
        }}
      >
        Logout
      </button>
    </div>
  );
};

const LoadingSpinner = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fafbff'
  }}>
    <div>Loading...</div>
  </div>
);

// Pull and clear the post-login redirect target saved by ProtectedRoute.
// Lets email links (e.g. the social-connection nudge → /onboarding/connectSocials)
// survive the round trip through Auth0 instead of dumping everyone on /dashboard.
/**
 * Coach paths (KGH-795).
 *
 * `/coach/signup` is deliberately NOT coach-only: an account becomes
 * user_type 'coach' by submitting that very form, so guarding it by role locks
 * every coach out of the product before they can join it. `/coach/pending` is
 * open for the same reason — it is where a just-registered account goes, at a
 * moment when the cached user object can still say it is not a coach. `/coach`
 * itself is the resolver (components/CoachRoute), which decides between
 * sign-up, the pending screen and the portal from GET /coach/me rather than
 * from user_type. All three answer to the API, which is the real authority.
 */
const COACH_OPEN_PATHS = ['/coach/signup', '/coach/pending', '/coach'];
const isCoachPath = (pathname: string): boolean =>
  pathname === '/coach' || pathname.startsWith('/coach/');
const isCoachOnlyPath = (pathname: string): boolean =>
  isCoachPath(pathname) && !COACH_OPEN_PATHS.includes(pathname);

const consumePostLoginRedirect = (): string | null => {
  try {
    const saved = sessionStorage.getItem('postLoginRedirect');
    if (saved) sessionStorage.removeItem('postLoginRedirect');
    return saved;
  } catch {
    return null;
  }
};

// Auth0 Callback handler - simple approach, let AuthContext handle the token exchange
const Auth0Callback = () => {
  const { isLoading, isAuthenticated, user } = useAuth();

  // Always show loading on callback - AuthContext will handle the redirect
  // The /callback URL has auth params that Auth0Provider processes automatically
  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    const saved = consumePostLoginRedirect();
    // A college coach has no family dashboard to land on (KGH-795): /dashboard
    // would bounce them straight back out via the guards below. A saved target
    // still wins, but only when it is a coach destination.
    if (user?.user_type === 'coach') {
      return <Navigate to={saved && saved.startsWith('/coach') ? saved : '/coach/players'} replace />;
    }
    return <Navigate to={saved || '/dashboard'} replace />;
  }

  // Not authenticated after loading complete - go to login
  return <Navigate to="/login" replace />;
};

// Protected Route wrapper with onboarding check
const ProtectedRoute = () => {
  const { isAuthenticated, isLoading, user, sessionToken } = useAuth();
  const { family } = useFamily();
  const { can, loading: permissionsLoading } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const [checkingOnboarding, setCheckingOnboarding] = React.useState(true);
  // The guards below ask can('*:*'). On a fresh page load (a link from an email)
  // that is false until permissions arrive, which sent super admins with an
  // athlete or guardian account from /staff/* to /dashboard. Wait for them.
  const waitingForPermissions = isAuthenticated && permissionsLoading;

  React.useEffect(() => {
    const checkOnboardingStatus = async () => {
      // Debug: Log user state
      console.log('🔍 ProtectedRoute check:', {
        hasUser: !!user,
        deleted_at: user?.deleted_at,
        isAuthenticated,
        sessionToken: !!sessionToken
      });

      // PRIORITY 1: Check if account is deleted
      if (user?.deleted_at) {
        console.log('🗑️ Account deleted, redirecting to deletion splash');
        navigate('/account-deleted', { replace: true });
        setCheckingOnboarding(false);
        return;
      }

      // Invitation routing used to live here as a guard that bounced
      // users to /invitation whenever invitationToken+invitationInfo
      // were in context. That decision tried to coordinate React state
      // from three independent contexts (AuthContext.user, InvitationContext,
      // React Router location) — they update at different ticks, the
      // guard read stale slices, and on the post-auto-accept path it
      // looped (/onboarding/quickStart → /invitation → preflight returns
      // already_member_redirect → back to /onboarding/quickStart → ...).
      //
      // Removed entirely. The invitation flow is self-contained:
      //   - Pre-auth: email link → /invite/:token → InvitationRouter
      //     (preflight, routes to /signup, /login, FamilyConflict, etc.)
      //   - Post-auth: backend auto-accepts during /auth/signup +
      //     /auth/login. The user's family_id is set server-side.
      //     No further client-side routing needed.
      //   - RootRedirect still routes "/" to /invitation when an
      //     invitation is pending but the user isn't authenticated.
      //
      // ProtectedRoute now just gates on auth + onboarding step.

      if (!isAuthenticated || !user || !sessionToken) {
        setCheckingOnboarding(false);
        return;
      }

      if (waitingForPermissions) return;

      try {
        // Get current onboarding status from backend
        const onboardingData = await userAPI.getOnboardingStep(sessionToken);
        
        console.log('🔍 Onboarding check:', {
          step: onboardingData.onboarding_step,
          complete: onboardingData.onboarding_complete,
          hasFamilyId: !!user?.family_id,
          currentPath: location.pathname
        });

        // If onboarding is complete (either by flag or step threshold), allow access
        // Staff uses STAFF_COMPLETE (220), athletes use COMPLETE (100)
        const completionThreshold = user.user_type === 'staff'
          ? ONBOARDING_STEPS.STAFF_COMPLETE
          : ONBOARDING_STEPS.COMPLETE;
        if (onboardingData.onboarding_complete || onboardingData.onboarding_step >= completionThreshold) {
          console.log('✅ Onboarding complete - allowing access');
          
          // Staff-specific redirects: if staff user tries athlete-only paths, redirect to staff dashboard
          // Super admins also get staff dashboard as their default landing page
          // Exception: admins who are also guardians/athletes get user nav
          const athleteOnlyPaths = ['/dashboard', '/profile', '/colleges', '/messages', '/analytics', '/tutorials'];
          // College coach (KGH-795): their whole product is /coach/*, and every
          // family and staff route refuses them at the API.
          if (user.user_type === 'coach' && athleteOnlyPaths.includes(location.pathname)) {
            navigate('/coach/players', { replace: true });
            setCheckingOnboarding(false);
            return;
          }
          // /coach/signup is the ONE coach path a non-coach must reach: an
          // account only becomes user_type 'coach' by submitting that form.
          if (user.user_type !== 'coach' && isCoachOnlyPath(location.pathname)) {
            navigate('/dashboard', { replace: true });
            setCheckingOnboarding(false);
            return;
          }
          const isAdminAlsoUser = can('*:*') && (user.user_type === 'guardian' || user.user_type === 'athlete');
          const isStaffOrAdmin = (user.user_type === 'staff' || can('*:*')) && !isAdminAlsoUser;
          if (isStaffOrAdmin && athleteOnlyPaths.includes(location.pathname)) {
            navigate('/staff/roster', { replace: true });
            setCheckingOnboarding(false);
            return;
          }
          
          // Reverse guard: if user has no staff access, redirect away from /staff/*.
          // Staff access = pure staff (user_type='staff'), super admin (*:*), OR a
          // guardian/athlete linked as org_staff (organization_id populated by /me).
          const hasStaffAccess = user.user_type === 'staff' || can('*:*') || !!user.organization_id;
          if (!hasStaffAccess && location.pathname.startsWith('/staff/')) {
            navigate('/dashboard', { replace: true });
            setCheckingOnboarding(false);
            return;
          }
          
          setCheckingOnboarding(false);
        } else {
          // Onboarding not complete, redirect to appropriate screen
          
          // Staff onboarding: steps 200-220
          if (user.user_type === 'staff') {
            if (!location.pathname.startsWith('/staff/onboarding')) {
              const staffStep = onboardingData.onboarding_step || 200;
              if (staffStep < 210) {
                navigate('/staff/onboarding/profile', { replace: true });
              } else if (staffStep < 220) {
                navigate('/staff/onboarding/connect', { replace: true });
              } else {
                navigate('/staff/roster', { replace: true });
              }
            }
            setCheckingOnboarding(false);
            return;
          }
          
          // Freemium-redesign Phase 1: any non-staff user with onboarding_step<100
          // belongs in the new quickstart flow. getScreenForStep still exists for
          // staff (returns 'staffOnboardingProfile' / 'staffOnboardingConnect')
          // but for athlete/guardian it's superseded by the single quickStart route.
          const isStaffStep = (onboardingData.onboarding_step || 0) >= 200;
          const target = isStaffStep
            ? `/onboarding/${getScreenForStep(onboardingData.onboarding_step || 0)}`
            : '/onboarding/quickStart';
          console.log('❌ Onboarding incomplete, redirecting to:', target);

          // If not trying to access an onboarding route, redirect to the appropriate
          // onboarding screen. /settings/* is a utility namespace (e.g. the social-connection
          // nudge email lands mid-onboarding users on /settings/connections) and bypasses
          // the step gate. /coach/* bypasses it too (KGH-795): a coach signing up has
          // onboarding_step 0 until POST /coach/register sets it to 100, and the family
          // quickstart refuses coach accounts outright — so sending them there is a
          // dead end on the first screen of the product.
          if (
            !location.pathname.startsWith('/onboarding') &&
            !location.pathname.startsWith('/settings') &&
            !isCoachPath(location.pathname)
          ) {
            navigate(target, { replace: true });
          }
          setCheckingOnboarding(false);
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        // Fallback: staff users go to staff dashboard, others to quickstart
        if (user.user_type === 'staff') {
          if (!location.pathname.startsWith('/staff')) {
            navigate('/staff/onboarding/profile', { replace: true });
          }
        } else if (!user.family_id && !isCoachPath(location.pathname) && user.user_type !== 'coach') {
          // A coach has no family_id and never will; this fallback would park
          // them in the family quickstart whenever the step check failed.
          navigate('/onboarding/quickStart', { replace: true });
        }
        setCheckingOnboarding(false);
      }
    };

    checkOnboardingStatus();
  // location.pathname included so guards re-fire on every navigation (not just auth changes)
  }, [isAuthenticated, user, sessionToken, navigate, family, location.pathname, waitingForPermissions]);

  if (isLoading || checkingOnboarding || waitingForPermissions) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    // Stash the intended URL so post-login lands here (e.g. email link to /onboarding/connectSocials).
    // sessionStorage survives the Auth0 round-trip in the same tab.
    const target = location.pathname + location.search;
    if (target && target !== '/' && !target.startsWith('/login') && !target.startsWith('/callback')) {
      try { sessionStorage.setItem('postLoginRedirect', target); } catch {}
    }
    /* A coach arriving at the one link they were given lands on a sign-in that
       says where they are going, rather than on the family product's pitch. */
    const loginPath = target.startsWith('/coach') ? '/login?coach=1' : '/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Cross-type route guards — must be in render path (not useEffect) so they
  // execute on every navigation, not just on mount. The useEffect above only
  // fires when auth state changes; without these render-time guards, a staff
  // user could navigate to /profile after initial load and bypass the check.
  const athleteOnlyPaths = ['/dashboard', '/profile', '/colleges', '/messages', '/analytics', '/tutorials'];
  // Coach guards, both directions (KGH-795). Without the mirror guard a family
  // user could open /coach/players and watch every call 403.
  if (user?.user_type === 'coach' && athleteOnlyPaths.includes(location.pathname)) {
    return <Navigate to="/coach/players" replace />;
  }
  if (user?.user_type !== 'coach' && isCoachOnlyPath(location.pathname)) {
    return <Navigate to="/dashboard" replace />;
  }
  const isAdminAlsoUser = can('*:*') && (user?.user_type === 'guardian' || user?.user_type === 'athlete');
  const isStaffOrAdmin = (user?.user_type === 'staff' || can('*:*')) && !isAdminAlsoUser;
  if (isStaffOrAdmin && athleteOnlyPaths.includes(location.pathname)) {
    return <Navigate to="/staff/roster" replace />;
  }
  if (user?.user_type !== 'staff' && !can('*:*') && location.pathname.startsWith('/staff/')) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
};

// Root redirect that checks for invitations
const RootRedirect = () => {
  const { invitationToken, invitationInfo } = useInvitation();
  
  // If there's an invitation, go to invitation page
  if (invitationToken && invitationInfo) {
    return <Navigate to="/invitation" replace />;
  }
  
  // Just go to login page - let user choose to sign in or sign up
  // Auth0's silent auth will auto-login if user has valid session
  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <BrowserRouter>
      <Auth0Provider
        domain={auth0Config.domain}
        clientId={auth0Config.clientId}
        authorizationParams={{
          redirect_uri: auth0Config.redirectUri,
          audience: auth0Config.audience,
          scope: auth0Config.scope,
        }}
        cacheLocation="localstorage"
        useRefreshTokens={false}
      >
        <AuthProvider>
          <PermissionsProvider>
            <FamilyProvider>
              <InvitationProvider>
                <ToastProvider>
                  <CalendarProvider>
                    <NotificationProvider>
                      <ReferralProvider>
                      <RedeemProvider>
                      <SearchProvider>
                      <SubscriptionProvider>
                      <UpgradeModal />
                      {/* Marks the document while a /coach/* page is on screen
                          so the coach surface can carry its own token values
                          (KGH-795: the WCAG-AA CTA fill). */}
                      <CoachSurfaceTheme />
                      <GamePlanEmailClickTracker />
                      <AppErrorBoundary>
                      <Suspense fallback={
                        <div style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          minHeight: '60vh', color: '#64748b', fontFamily: 'Manrope, sans-serif',
                        }}>
                          Loading…
                        </div>
                      }>
                      <Routes>
                  {/* Public Routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignUpPage />} />
                  <Route path="/callback" element={<Auth0Callback />} />
                  <Route path="/oauth-callback" element={<OAuthCallback />} />
                  <Route path="/email-verified" element={<EmailVerifiedPage />} />
                  <Route path="/athlete/:slug" element={<PublicProfile />} />
                  {/* Public school page (Phase 3 §3.4) — unauthenticated, PII-safe. */}
                  <Route path="/school/:slug" element={<PublicSchool />} />

                  {/* Promo/access-code redemption (Phase 0.5). Public: works
                      logged in (redeem inline) or logged out (capture + auto-
                      redeem after signup via RedeemContext). */}
                  <Route path="/redeem" element={<RedeemPage />} />
                  <Route path="/redeem/:code" element={<RedeemPage />} />
                  
                  {/* Account Deletion - Accessible to deleted users */}
                  <Route path="/account-deleted" element={<AccountDeletionSplash />} />
                  
                  {/* Subscription Required - Protected but no subscription check */}
                  <Route path="/subscription-required" element={<SubscriptionRequired />} />
                  
                  {/* Invitation Route - Can be accessed with or without auth */}
                  {/* InvitationRouter handles the pre-flight + destination
                      routing. For the email-mismatch case it falls through
                      to rendering InvitationWelcome inline. */}
                  <Route path="/invitation" element={<InvitationRouter />} />
                  {/* Email links from the backend land at /invite/{token}.
                      Route directly to InvitationRouter (not through a
                      Navigate shim) — the old shim mutated the URL before
                      InvitationContext's URL-detection effect could read
                      the token, so the token never landed in localStorage
                      and the recipient saw "Invitation Unavailable" on
                      every fresh invite. */}
                  <Route path="/invite/:token" element={<InvitationRouter />} />
                  
                  {/* Protected Routes */}
                  <Route element={<ProtectedRoute />}>
                    {/* New flow (freemium-redesign Phase 1 C1): single quickstart screen */}
                    <Route path="/onboarding/quickStart" element={<OnboardingQuickStart />} />

                    {/* Legacy /onboarding/* route shims (Phase 1 D2). Pre-freemium-deploy
                        users with in-flight Stripe sessions and email-nudge links keep working:
                          - connectSocials → /settings/connections (matches the comment above)
                          - inviteFamilyMembers → /dashboard?checkout_success=true (Stripe success
                            URL fix already in stripe-checkout.js:144 lands here)
                          - subscription → /subscription-required?cancelled=true (Stripe cancel
                            URL fix already in stripe-checkout.js:146 lands here)
                          - everything else → /onboarding/quickStart
                        Phase 2 removes shims after mobile-binary lifecycle clears. */}
                    <Route path="/onboarding/connectSocials" element={<Navigate to="/settings/connections" replace />} />
                    <Route path="/onboarding/subscription" element={<Navigate to="/subscription-required?cancelled=true" replace />} />
                    <Route path="/onboarding/familySetup" element={<Navigate to="/onboarding/quickStart" replace />} />
                    <Route path="/onboarding/userType" element={<Navigate to="/onboarding/quickStart" replace />} />
                    <Route path="/onboarding/athleteProfile" element={<Navigate to="/onboarding/quickStart" replace />} />
                    <Route path="/onboarding/schoolInfo" element={<Navigate to="/onboarding/quickStart" replace />} />
                    <Route path="/onboarding/sportInfo" element={<Navigate to="/onboarding/quickStart" replace />} />
                    <Route path="/onboarding/academicPerformance" element={<Navigate to="/onboarding/quickStart" replace />} />
                    <Route path="/onboarding/sportPerformance" element={<Navigate to="/onboarding/quickStart" replace />} />
                    <Route path="/onboarding/profilePic" element={<Navigate to="/onboarding/quickStart" replace />} />
                    <Route path="/onboarding/complete" element={<Navigate to="/dashboard" replace />} />

                    {/* Settings / account utility routes (no subscription gate — reachable from
                        nudge emails even if onboarding or subscription is incomplete) */}
                    <Route path="/settings/connections" element={<Connections />} />
                    <Route path="/settings/notifications" element={<NotificationSettings />} />

                    {/* Main app routes — freemium-redesign Phase 1 D1: SubscriptionGate
                        removed from /dashboard, /profile, /colleges, /tutorials.
                        Per-action gating via useSubscriptionGuard inside components.
                        /messages and /analytics stay gated (entire feature is paid) —
                        free users get the upgrade modal floated over /dashboard.
                        The athleteOnlyPaths array below at line 287 stays unchanged —
                        Phase 1 alters route gating, not route names. */}
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/profile" element={<PlayerProfile />} />
                    <Route path="/colleges" element={<CollegeLists />} />
                    {/* Messages is open to free users now — the paywall is the
                        3-program cap (enforced at send/unlock), not the surface. */}
                    <Route path="/messages" element={<Messages />} />
                    <Route path="/analytics" element={<SubscriptionGate><Analytics /></SubscriptionGate>} />
                    <Route path="/tutorials" element={<Tutorials />} />
                    
                    {/* Admin Routes - Also protected by subscription gate */}
                    <Route path="/admin/users" element={<SubscriptionGate><AdminUsers /></SubscriptionGate>} />
                    <Route path="/admin/manage-athletes" element={<SubscriptionGate><AdminManageAthletes /></SubscriptionGate>} />
                    <Route path="/admin/manage-athletes/:athleteId" element={<SubscriptionGate><AdminAthleteProfile /></SubscriptionGate>} />
                    <Route path="/admin/queues" element={<SubscriptionGate><AdminQueues /></SubscriptionGate>} />
                    <Route path="/admin/promo-codes" element={<SubscriptionGate><AdminPromoCodes /></SubscriptionGate>} />
                    <Route path="/admin/partners" element={<SubscriptionGate><AdminPartners /></SubscriptionGate>} />
                    <Route path="/admin/partners/:partnerId" element={<SubscriptionGate><AdminPartnerDetail /></SubscriptionGate>} />
                    <Route path="/admin/payouts" element={<SubscriptionGate><AdminPayouts /></SubscriptionGate>} />
                    <Route path="/admin/organizations" element={<SubscriptionGate><AdminOrganizations /></SubscriptionGate>} />
                    <Route path="/admin/organizations/:orgId" element={<SubscriptionGate><AdminOrgDetail /></SubscriptionGate>} />
                    <Route path="/admin/analytics" element={<SubscriptionGate><AdminAnalytics /></SubscriptionGate>} />
                    <Route path="/admin/coaches" element={<SubscriptionGate><AdminCoaches /></SubscriptionGate>} />
                    {/* Staging only. On a production build AdminEmailOutbox is
                        null, so this route does not exist there. */}
                    {AdminEmailOutbox && (
                      <Route path="/admin/outbox" element={<SubscriptionGate><AdminEmailOutbox /></SubscriptionGate>} />
                    )}

                    {/* College Coach Routes (KGH-795). NO SubscriptionGate, on
                        purpose: a coach is not a paying family, the backend
                        mounts no requirePaidFamily on any /coach route, and a
                        402 would read as a broken paywall to someone who is not
                        being charged. */}
                    {/* Entry: reachable before the account is a coach at all. */}
                    <Route path="/coach/signup" element={<CoachSignup />} />
                    {/* Waiting room: pending, rejected and suspended accounts. */}
                    <Route path="/coach/pending" element={<CoachPending />} />
                    {/* Every verified-coach page goes through the SAME gate. The
                        grid used to render its own inline "we are verifying you"
                        card, which meant a pending coach landing here after login
                        never saw the pending screen that was designed for them
                        (with the submission date, a re-check and a support
                        route). One treatment, one place. */}
                    <Route path="/coach/players" element={<CoachRoute><CoachPlayers /></CoachRoute>} />
                    {/* The profile is a WRITE on open (it notifies the athlete's
                        family), so it is reached only by an explicit navigation
                        from the grid or a list — never prefetched. */}
                    <Route path="/coach/players/:athleteId" element={<CoachRoute><CoachPlayerProfile /></CoachRoute>} />
                    {/* APS-255 items 6 + 11. The browse and one event are the
                        same page: the event id is the only difference, and
                        splitting them would duplicate the whole data contract
                        for the sake of two components. */}
                    <Route path="/coach/tournaments" element={<CoachRoute><CoachTournaments /></CoachRoute>} />
                    <Route path="/coach/tournaments/:eventId" element={<CoachRoute><CoachTournaments /></CoachRoute>} />
                    <Route path="/coach/lists" element={<CoachRoute><CoachLists /></CoachRoute>} />
                    {/* A list is addressable, the way a tournament and a player
                        profile already are. Before this, /coach/lists/<id> fell
                        through the /coach/* catch-all below and silently landed
                        the coach on the players grid. */}
                    <Route path="/coach/lists/:listId" element={<CoachRoute><CoachLists /></CoachRoute>} />
                    <Route path="/coach/preferences" element={<CoachRoute><CoachSettings /></CoachRoute>} />
                    {/* `/coach` and anything under it that is not mounted yet
                        resolve through the same guard, so a coach can never
                        fall into the family catch-all below (which would send
                        them to /login, silently re-authenticate them, and land
                        them back here — a loop with nothing on screen). */}
                    <Route path="/coach" element={<CoachHome />} />
                    <Route path="/coach/*" element={<CoachHome />} />

                    {/* Staff Onboarding Routes - No SubscriptionGate */}
                    <Route path="/staff/onboarding/profile" element={<StaffOnboardingProfile />} />
                    <Route path="/staff/onboarding/connect" element={<StaffOnboardingConnectSocials />} />

                    {/* Staff Routes - No SubscriptionGate (staff don't need subscriptions) */}
                    <Route path="/staff/dashboard" element={<Navigate to="/staff/roster" replace />} />
                    <Route path="/staff/roster" element={<StaffRoster />} />
                    <Route path="/staff/player/:athleteId" element={<StaffAthleteProfile />} />
                    <Route path="/staff/messages" element={<StaffMessages />} />
                    {/* compose + bulk-compose collapsed into the single messages route —
                        both rendered the identical StaffMessages with no differentiating props. */}
                    <Route path="/staff/compose" element={<Navigate to="/staff/messages" replace />} />
                    <Route path="/staff/bulk-compose" element={<Navigate to="/staff/messages" replace />} />
                    <Route path="/staff/analytics" element={<StaffAnalytics />} />
                    <Route path="/staff/lists" element={<StaffLists />} />
                    <Route path="/staff/all-schools" element={<StaffAllSchools />} />
                    <Route path="/staff/schools" element={<StaffSchoolAggregation />} />
                  </Route>
                  
                  {/* Default redirect */}
                  <Route path="/" element={<RootRedirect />} />
                  {/* Catch-all — any unknown path runs through the same
                      root-redirect logic (which surfaces a pending
                      invitation if present). Prevents blank screens on
                      typos or stale email links. */}
                  <Route path="*" element={<RootRedirect />} />
                </Routes>
                </Suspense>
                </AppErrorBoundary>
                </SubscriptionProvider>
                </SearchProvider>
                </RedeemProvider>
                </ReferralProvider>
                </NotificationProvider>
                </CalendarProvider>
              </ToastProvider>
            </InvitationProvider>
          </FamilyProvider>
          </PermissionsProvider>
        </AuthProvider>
      </Auth0Provider>
    </BrowserRouter>
  );
}

export default App;
